/**
 * `.in('col', ids)` 를 여러 번 나눠 실행하고 결과를 합친다.
 *
 * PostgREST는 필터를 URL 쿼리스트링에 담는다. id 목록이 길어지면 URL 자체가
 * 게이트웨이 허용 길이를 넘고, 그 순간 요청은 DB에 닿지도 못한 채 400으로 끊긴다.
 * 2026-08-18 2학기 오픈으로 applications가 1,397건이 되자 관리자 신청관리의
 * 환불정보 조회(URL 39KB)가 하루 380회 400으로 실패했다. 코드가 error를 버리고
 * 있었기 때문에 화면은 "환불 없음"처럼 조용히 잘못 표시됐다.
 *
 * 목록 길이는 데이터가 쌓이면 늘어난다. 지금 안 터지는 곳도 언젠가 터진다는 뜻이라
 * 사용자 수·신청 수에 비례해 자라는 `.in()` 은 전부 이 함수를 거친다.
 *
 * UUID 1개는 인코딩 후 39자(36 + %2C)를 차지한다. 200개면 약 7.8KB로
 * 어떤 게이트웨이 설정에서도 안전하다.
 */

const DEFAULT_CHUNK_SIZE = 200;

export function chunk<T>(values: T[], size: number = DEFAULT_CHUNK_SIZE): T[][] {
  if (values.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    out.push(values.slice(i, i + size));
  }
  return out;
}

/**
 * @param values in() 에 넣을 값 전체
 * @param run    값 일부를 받아 쿼리를 실행하는 함수 (PostgrestBuilder는 1회용이라 함수로 받는다)
 * @param size   한 번에 보낼 값 개수
 * @returns 합쳐진 data. 조각 하나라도 실패하면 그 error를 함께 돌려준다(부분 결과도 유지).
 */
export async function selectInChunks<T, V = string>(
  values: V[],
  run: (slice: V[]) => PromiseLike<{ data: T[] | null; error: any }>,
  size: number = DEFAULT_CHUNK_SIZE
): Promise<{ data: T[]; error: any }> {
  const slices = chunk(values, size);
  if (slices.length === 0) return { data: [], error: null };

  const results = await Promise.all(slices.map(slice => run(slice)));

  const data: T[] = [];
  let error: any = null;
  for (const result of results) {
    if (result.error && !error) error = result.error;
    if (result.data) data.push(...result.data);
  }

  return { data, error };
}

/**
 * PostgREST는 한 요청에 최대 1,000행만 돌려준다(프로젝트 설정 max_rows=1000).
 * 초과분은 에러가 아니라 조용히 잘린다. applications가 1,397건이 된 뒤로
 * 관리자 신청관리와 통계는 오래된 397건을 못 보고 있었다.
 *
 * 전체가 필요한 조회는 range()로 끝까지 넘긴다.
 * 페이지 경계가 흔들리지 않도록 호출부에서 고유 컬럼(id) 정렬을 함께 걸어야 한다.
 */
const PAGE_SIZE = 1000;

export async function selectAllPages<T>(
  run: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>,
  pageSize: number = PAGE_SIZE
): Promise<{ data: T[]; error: any }> {
  const data: T[] = [];

  for (let page = 0; ; page++) {
    const from = page * pageSize;
    const { data: rows, error } = await run(from, from + pageSize - 1);

    if (error) return { data, error };
    if (!rows || rows.length === 0) break;

    data.push(...rows);
    if (rows.length < pageSize) break;
  }

  return { data, error: null };
}
